import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  TextInput,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService, DataList } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { FilterField, fv, QuickView, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';

const SYSTEM_MODULES = ['contacts', 'products', 'catalog', 'calendar', 'orders', 'vendors', 'quotations'];

const PAGE_SIZE = 20;

export const DataListsScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const goBack = useGoBack('home');
  const { t } = useLocale();
  const rtl = useRTLStyles();

  // Standard full-layout panes (settings drawer on desktop web), matching
  // every other list screen (e.g. ContactsListScreen.tsx) — previously
  // omitted here, leaving desktop-web users without the settings pane.

  const [lists, setLists] = useState<DataList[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    { key: 'custom', label: t('dataLists.view.custom'), icon: 'construct-outline', filters: { columnsSource: 'custom' } },
    { key: 'system', label: t('dataLists.view.system'), icon: 'apps-outline', filters: { columnsSource: 'system' } },
    { key: 'webhook', label: t('dataLists.view.webhook'), icon: 'git-network-outline', filters: { dataSources: ['webhook'] } },
    { key: 'empty', label: t('dataLists.view.empty'), icon: 'file-tray-outline', filters: { hasRecords: 'no' } },
  ], [t]);
  const list = useListFilters('data-lists', { quickViews });
  const search = list.state.search;
  const [showFilters, setShowFilters] = useState(false);
  const listFields = useMemo<FilterField[]>(() => [
    { key: 'columnsSource', label: t('dataLists.filter.type'), type: 'choice', options: [{ value: 'custom', label: t('dataLists.view.custom') }, { value: 'system', label: t('dataLists.view.system') }] },
    { key: 'systemModules', label: t('dataLists.filter.module'), type: 'multi', options: SYSTEM_MODULES.map((m) => ({ value: m, label: t(`dataLists.schema.systemModule.${m}` as any) })) },
    { key: 'dataSources', label: t('dataLists.filter.source'), type: 'multi', options: [{ value: 'manual', label: t('dataLists.filter.sourceManual') }, { value: 'webhook', label: t('dataLists.filter.sourceWebhook') }] },
    { key: 'hasRecords', label: t('dataLists.filter.records'), type: 'choice', options: [{ value: 'yes', label: t('dataLists.filter.hasRecords') }, { value: 'no', label: t('dataLists.filter.noRecords') }] },
    { key: 'createdAt', label: t('dataLists.filter.added'), type: 'date' },
  ], [t]);
  const listSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('dataLists.sort.newest'), sortBy: 'createdAt', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'name', label: t('dataLists.sort.name'), sortBy: 'name', sortOrder: 'asc', icon: 'text-outline' },
    { key: 'updated', label: t('dataLists.sort.updated'), sortBy: 'updatedAt', sortOrder: 'desc', icon: 'time-outline' },
  ], [t]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listToDelete, setListToDelete] = useState<DataList | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadLists = useCallback(
    async (pageToLoad = 0, reset = false) => {
      try {
        setError(null);
        if (reset) setLoading(true);
        const f = list.state.filters;
        const sort = listSorts.find((x) => x.key === list.state.sort) || listSorts[0];
        const res = await apiService.getDataLists({
          search: search.trim() || undefined,
          limit: PAGE_SIZE,
          offset: pageToLoad * PAGE_SIZE,
          columnsSource: f.columnsSource,
          systemModules: fv.csv(f.systemModules),
          dataSources: fv.csv(f.dataSources),
          hasRecords: f.hasRecords,
          createdFrom: f.createdAt?.from ? `${f.createdAt.from}T00:00:00.000` : undefined,
          createdTo: f.createdAt?.to ? `${f.createdAt.to}T23:59:59.999` : undefined,
          sortBy: sort.sortBy as any,
          sortOrder: sort.sortOrder,
        });
        if (res.statusCode === 1000 && Array.isArray(res.data)) {
          const incoming = res.data;
          setLists((prev) => (reset ? incoming : [...prev, ...incoming]));
          setTotal(res.additionalInfo?.total ?? incoming.length);
          setHasMore(incoming.length === PAGE_SIZE);
          setPage(pageToLoad);
        } else {
          const msg = res.errors?.[0]?.message || t('dataLists.error.loadFailed');
          setError(msg);
          if (reset) setLists([]);
        }
      } catch (err: any) {
        setError(err?.message || t('dataLists.error.loadFailed'));
        if (reset) setLists([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [search, t, list.state, listSorts]
  );

  const filterKey = JSON.stringify([list.state.filters, list.state.sort, search]);
  useEffect(() => {
    if (!list.ready) return;
    const timer = setTimeout(() => loadLists(0, true), search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.ready, filterKey]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadLists(0, true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && !error) loadLists(page + 1, false);
  };

  const confirmDelete = async () => {
    if (!listToDelete) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteDataList(listToDelete._id);
      if (res.statusCode === 1000) {
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.delete.success'));
        setShowDeleteModal(false);
        setListToDelete(null);
        loadLists(0, true);
      } else {
        const msg = res.errors?.[0]?.message || t('dataLists.delete.failed');
        showNotification('error', t('dataLists.notifications.errorTitle'), msg);
        setShowDeleteModal(false);
        setListToDelete(null);
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.delete.failed'));
      setShowDeleteModal(false);
      setListToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const renderCard = ({ item }: { item: DataList }) => {
    const columnCount = item.columns?.length || 0;
    return (
      <TouchableOpacity
        style={styles.card}
        activeOpacity={0.7}
        onPress={() => navigateTo('data-list-records', item._id)}
      >
        <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.cardIcon}>
            <Ionicons name="server-outline" size={22} color={colors.primary} />
          </View>
          <View style={styles.cardInfo}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {item.name}
            </Text>
            {item.description ? (
              <Text style={[styles.cardDescription, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {item.description}
              </Text>
            ) : null}
            <View style={[styles.cardMetaRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.cardMeta}>
                {columnCount === 1 ? t('dataLists.card.columnsOne') : t('dataLists.card.columns', { count: columnCount })}
              </Text>
              <Text style={styles.cardMetaDot}> · </Text>
              <Text style={styles.cardMeta}>
                {item.hasRecords ? t('dataLists.card.hasRecords') : t('dataLists.card.noRecords')}
              </Text>
            </View>
          </View>
        </View>

        <View style={[styles.cardActions, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity
            style={[styles.cardActionButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => navigateTo('data-list-records', item._id)}
          >
            <Ionicons name="list-outline" size={16} color={colors.primary} />
            <Text style={styles.cardActionText}>{t('dataLists.actions.records')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cardActionButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => navigateTo('data-list-schema-edit', item._id)}
          >
            <Ionicons name="create-outline" size={16} color={colors.primary} />
            <Text style={styles.cardActionText}>{t('dataLists.actions.edit')}</Text>
          </TouchableOpacity>
          <View style={{ flex: 1 }} />
          <TouchableOpacity
            style={styles.cardDeleteButton}
            onPress={() => {
              setListToDelete(item);
              setShowDeleteModal(true);
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="trash-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderContent = () => {
    if (loading && lists.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('dataLists.loading')}</Text>
        </View>
      );
    }
    if (error && lists.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>{t('dataLists.error.loadTitle')}</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button title={t('common.retry')} onPress={() => loadLists(0, true)} variant="primary" style={styles.retryButton} />
        </View>
      );
    }
    if (lists.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="server-outline" size={64} color={colors.textLight} />
          <Text style={styles.errorTitle}>{t('dataLists.empty.title')}</Text>
          <Text style={styles.errorText}>{t('dataLists.empty.message')}</Text>
          <Button title={t('dataLists.create')} onPress={() => navigateTo('data-list-schema-create')} variant="primary" style={styles.retryButton} />
        </View>
      );
    }
    return (
      <FlatList
        data={lists}
        renderItem={renderCard}
        keyExtractor={(item) => item._id}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.4}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary as string]} />}
      />
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={t('dataLists.title')}
          subtitle={!loading ? (total === 1 ? t('dataLists.subtitle.countOne') : t('dataLists.subtitle.count', { count: total })) : undefined}
          onBack={goBack}
          actions={[{ icon: 'add', primary: true, onPress: () => navigateTo('data-list-schema-create'), label: t('dataLists.create'), accessibilityLabel: t('dataLists.create') }]}
        />
      }
    >
      <View style={styles.container}>
        <ListFilterBar
          controller={list}
          fields={listFields}
          sorts={listSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('dataLists.searchPlaceholder')}
        />

        {renderContent()}

        <FilterSheet
          visible={showFilters}
          title={t('dataLists.filter.title')}
          fields={listFields}
          sorts={listSorts}
          value={list.state}
          onApply={list.apply}
          onClose={() => setShowFilters(false)}
        />

        <ConfirmationModal
          visible={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setListToDelete(null);
          }}
          onConfirm={confirmDelete}
          title={t('dataLists.delete.title')}
          message={listToDelete ? t('dataLists.delete.message', { name: listToDelete.name }) : ''}
          confirmText={t('dataLists.delete.confirm')}
          cancelText={t('dataLists.delete.cancel')}
          type="error"
          loading={deleting}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    ...designTokens.shadows.sm,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    paddingVertical: 0,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: String(colors.primary) + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardInfo: { flex: 1 },
  cardTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  cardDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  cardMetaRow: { flexDirection: 'row', alignItems: 'center', marginTop: spacing.xs },
  cardMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardMetaDot: { color: colors.textLight, fontSize: typography.fontSize.xs },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cardActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: String(colors.primary) + '10',
  },
  cardActionText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  cardDeleteButton: { padding: spacing.xs },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'center',
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryButton: { minWidth: 160 },
});
